import React from "react";
import {
  Box,
  Text,
  Stack,
  Flex,
  HStack,
  IconButton,
  Tooltip,
  Icon,
} from "@chakra-ui/react";
import { FiTrash2, FiShield } from "react-icons/fi";
import {
  CategoryPill,
  MedicineImage,
  PriceTag,
  QuantityStepper,
  RxBadge,
} from "../../UI/Primitives";
import { colors } from "../../UI/designSystem";

const getMedId = (med) =>
  med?._id || med?.id || med?.product_id || med?.productId || med?.Name || med?.product_name;

const AddedMedicineList = ({ addedMeds, isPrescriptionRequired, setAddedMeds }) => {
  return (
    <Box mt={addedMeds?.length ? 3 : 0}>
      {!addedMeds?.length ? null : (
        <Stack
          spacing={2}
          maxH={{ base: "340px", xl: "420px" }}
          overflowY="auto"
          pr={1}
          sx={{
            "&::-webkit-scrollbar": { width: "8px" },
            "&::-webkit-scrollbar-thumb": { background: "#dbe3ef", borderRadius: "999px" },
          }}
        >
          {addedMeds.map((m) => {
            const id = getMedId(m);
            const qty = m.qty || 1;
            const name = m.product_name || m.Name || m.name || "Unnamed medicine";
            const isRx = isPrescriptionRequired(m);
            const genericName =
              m.generic_name ||
              m.extracted_generic_name ||
              m.composition ||
              m.key_ingredients ||
              "Generic details unavailable";
            const price = Number(m.mrp || m.Price || m.price || 0);
            const mrp = price > 0 ? Math.round(price * 1.18) : undefined;
            const hasSavings = isRx || /paracetamol|amoxicillin|cetirizine|metformin|atorvastatin/i.test(`${name} ${genericName}`);
            const cheaperOptions = [
              `${name.split(" ")[0]} generic`,
              genericName.split(",")[0] || "Equivalent substitute",
            ].filter(Boolean);
            return (
              <Box
                key={id}
                px={3}
                py={3}
                bg="#ffffff"
                border="1px solid #e8edf3"
                borderRadius="14px"
                boxShadow="0 2px 8px rgba(15,23,42,0.03)"
                _hover={{ borderColor: "#d7e0ea", boxShadow: "0 10px 20px rgba(15,23,42,0.06)" }}
                transition="all 0.12s"
              >
                <Flex gap={3} align="flex-start">
                  <MedicineImage med={m} size={56} rounded="md" />
                  <Box flex={1} minW={0}>
                    <Flex align="flex-start" justify="space-between" gap={3} wrap="wrap">
                      <Box minW={0} flex={1}>
                        <HStack spacing={1.5} mb={1.5} wrap="wrap">
                          <CategoryPill med={m} />
                          {isRx ? <RxBadge /> : null}
                        </HStack>
                        <Text
                          fontWeight="800"
                          color="#0f172a"
                          fontSize="14px"
                          noOfLines={1}
                          m={0}
                          title={name}
                        >
                          {name}
                        </Text>
                        <Text fontSize="11px" color="#64748b" m={0} mt={0.5} noOfLines={1}>
                          {genericName}
                        </Text>
                        {hasSavings ? (
                          <HStack
                            mt={1.5}
                            spacing={1}
                            display="inline-flex"
                            bg={colors.brand[50]}
                            color={colors.brand[700]}
                            border={`1px solid ${colors.brand[100]}`}
                            borderRadius="full"
                            px={2}
                            py="3px"
                          >
                            <Icon as={FiShield} boxSize={2.5} />
                            <Text fontSize="10px" fontWeight="800" m={0}>
                              Generic substitute, save 40%
                            </Text>
                          </HStack>
                        ) : null}
                      </Box>
                      <Tooltip label="Remove" placement="top" hasArrow>
                        <IconButton
                          aria-label="remove"
                          icon={<FiTrash2 size={14} />}
                          size="sm"
                          variant="ghost"
                          color="#94a3b8"
                          _hover={{ color: "#dc2626", bg: "#fff1f2" }}
                          borderRadius="full"
                          onClick={() =>
                            setAddedMeds((prev) => prev.filter((x) => getMedId(x) !== id))
                          }
                        />
                      </Tooltip>
                    </Flex>

                    <Flex mt={2} align={{ base: "center", md: "center" }} justify="space-between" gap={2} wrap="nowrap">
                      <PriceTag price={price} mrp={mrp} size="sm" />
                      <QuantityStepper
                        value={qty}
                        onChange={(value) =>
                          setAddedMeds((prev) =>
                            prev.map((x) =>
                              getMedId(x) === id ? { ...x, qty: value } : x
                            )
                          )
                        }
                      />
                    </Flex>

                    {cheaperOptions.length ? (
                      <Box mt={2} bg="#f8fafc" border="1px solid #eef2f7" borderRadius="10px" px={2.5} py={2}>
                        <HStack spacing={1.5} wrap="wrap">
                          {cheaperOptions.map((option) => (
                            <Box
                              key={option}
                              as="span"
                              fontSize="10px"
                              fontWeight="700"
                              color="#0f766e"
                              bg="#ecfeff"
                              px={2}
                              py="3px"
                              borderRadius="full"
                            >
                              {option}
                            </Box>
                          ))}
                        </HStack>
                      </Box>
                    ) : null}
                  </Box>
                </Flex>
              </Box>
            );
          })}
        </Stack>
      )}
    </Box>
  );
};

export default AddedMedicineList;
export { getMedId };
